import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Lock, Network, Link2, RefreshCw, Check, Trash2, Save, Camera, UserPlus, X, Building2, Move, Maximize2, Expand, Loader2 } from 'lucide-react';
import { Channel, ChannelVisibility } from '../types/channel';
import { useChannel } from '../hooks/useChannel';
import { useChannelShare } from '../hooks/useChannelShare';
import { apiClient } from '../lib/apiClient';
import BodyPortal from './BodyPortal';

// ─── Banner Crop Modal ────────────────────────────────────────────────────────
// Aspect ratio of the banner preview (matches ChannelHeader banner height).
const BANNER_ASPECT = 4.5; // width / height

interface BannerCropModalProps {
  src: string;             // object URL of the selected file
  onConfirm: (blob: Blob) => void;
  onCancel: () => void;
}

const BannerCropModal: React.FC<BannerCropModalProps> = ({ src, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  // Natural image dimensions
  const [imgNatural, setImgNatural] = useState<{ w: number; h: number } | null>(null);
  // Container display dimensions (set after mount via ResizeObserver)
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);
  // scale: display pixels per natural image pixel. Controls zoom level.
  const [scale, setScale] = useState<number>(1);
  // Offset: top-left of the image in display space (can be positive in fit mode).
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; startOX: number; startOY: number } | null>(null);

  // Observe the container width.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      const w = e.contentRect.width;
      const h = w / BANNER_ASPECT;
      setContainerSize({ w, h });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // "Cover" mode: scale to fill width, centre vertically. Used as initial state.
  const applyCover = useCallback((nat: { w: number; h: number }, cs: { w: number; h: number }) => {
    const s = cs.w / nat.w;
    const displayH = nat.h * s;
    setScale(s);
    setOffset({ x: 0, y: -(displayH - cs.h) / 2 });
  }, []);

  // "Fit" mode: scale so entire image is visible, centre in frame.
  const applyFit = useCallback((nat: { w: number; h: number }, cs: { w: number; h: number }) => {
    const s = Math.min(cs.w / nat.w, cs.h / nat.h);
    const dw = nat.w * s;
    const dh = nat.h * s;
    setScale(s);
    setOffset({ x: (cs.w - dw) / 2, y: (cs.h - dh) / 2 });
  }, []);

  // Once dimensions are known, start in cover mode.
  useEffect(() => {
    if (!imgNatural || !containerSize) return;
    applyCover(imgNatural, containerSize);
  }, [imgNatural, containerSize, applyCover]);

  // Drag handler — free movement, no forced clamping in fit mode.
  const startDrag = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragRef.current = { startX: clientX, startY: clientY, startOX: offset.x, startOY: offset.y };

    const onMove = (ev: MouseEvent | TouchEvent) => {
      if (!dragRef.current) return;
      const cx = 'touches' in ev ? ev.touches[0].clientX : (ev as MouseEvent).clientX;
      const cy = 'touches' in ev ? ev.touches[0].clientY : (ev as MouseEvent).clientY;
      const dx = cx - dragRef.current.startX;
      const dy = cy - dragRef.current.startY;
      setOffset({ x: dragRef.current.startOX + dx, y: dragRef.current.startOY + dy });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
  };

  const handleConfirm = () => {
    if (!imgNatural || !containerSize) return;

    // Convert display-space position to image-space crop rect.
    // cropX/Y are the image coordinates at the top-left of the container.
    const cropX = -offset.x / scale;
    const cropY = -offset.y / scale;
    const cropW = containerSize.w / scale;
    const cropH = containerSize.h / scale;

    // Output canvas always matches the banner aspect ratio.
    const canvasW = 2400;
    const canvasH = Math.round(canvasW / BANNER_ASPECT);
    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d')!;

    // Black background for areas outside the image (fit mode letterboxing).
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvasW, canvasH);

    const img = new Image();
    img.src = src;
    img.onload = () => {
      // Clamp source rect to image bounds, and map to destination accordingly.
      const srcX = Math.max(0, cropX);
      const srcY = Math.max(0, cropY);
      const srcX2 = Math.min(imgNatural.w, cropX + cropW);
      const srcY2 = Math.min(imgNatural.h, cropY + cropH);
      if (srcX2 <= srcX || srcY2 <= srcY) {
        canvas.toBlob(blob => { if (blob) onConfirm(blob); }, 'image/jpeg', 0.92);
        return;
      }
      // Map source rect to canvas destination proportionally.
      const dstX = ((srcX - cropX) / cropW) * canvasW;
      const dstY = ((srcY - cropY) / cropH) * canvasH;
      const dstW = ((srcX2 - srcX) / cropW) * canvasW;
      const dstH = ((srcY2 - srcY) / cropH) * canvasH;
      ctx.drawImage(img, srcX, srcY, srcX2 - srcX, srcY2 - srcY, dstX, dstY, dstW, dstH);
      canvas.toBlob(blob => { if (blob) onConfirm(blob); }, 'image/jpeg', 0.92);
    };
  };

  const imgDisplayW = imgNatural ? imgNatural.w * scale : 0;
  const imgDisplayH = imgNatural ? imgNatural.h * scale : 0;
  const ready = !!(imgNatural && containerSize);

  const btnBase: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500,
    cursor: 'pointer', border: '1px solid var(--border, #e5e7eb)',
    background: 'var(--controlsBg, #f9fafb)', color: 'var(--foreground, #111)',
    transition: 'background 0.15s',
  };

  return (
    <BodyPortal>
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100,
      background: 'rgba(0,0,0,0.72)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: 'var(--card, #fff)',
        borderRadius: 12,
        width: '100%', maxWidth: 640,
        boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px', borderBottom: '1px solid var(--border, #e5e7eb)',
        }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--foreground, #111)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Move size={15} aria-hidden />
            {t('channel.crop_banner_title', 'Ajustar portada')}
          </span>
          <button onClick={onCancel} aria-label={t('common.cancel', 'Cancelar')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--foreground, #111)', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Toolbar — fit / cover quick actions */}
        <div style={{ display: 'flex', gap: 8, padding: '10px 16px 0', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--textSecondary, #6b7280)', marginRight: 4 }}>
            {t('channel.crop_banner_hint', 'Arrastra para reposicionar')}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              onClick={() => imgNatural && containerSize && applyFit(imgNatural, containerSize)}
              disabled={!ready}
              title={t('channel.crop_fit_tooltip', 'Ver imagen completa dentro del recuadro')}
              style={{ ...btnBase, opacity: ready ? 1 : 0.4 }}
            >
              <Expand size={13} aria-hidden />
              {t('channel.crop_fit', 'Ajustar')}
            </button>
            <button
              onClick={() => imgNatural && containerSize && applyCover(imgNatural, containerSize)}
              disabled={!ready}
              title={t('channel.crop_cover_tooltip', 'Llenar el recuadro con la imagen')}
              style={{ ...btnBase, opacity: ready ? 1 : 0.4 }}
            >
              <Maximize2 size={13} aria-hidden />
              {t('channel.crop_cover', 'Llenar')}
            </button>
          </div>
        </div>

        {/* Crop area */}
        <div style={{ padding: '10px 16px 8px' }}>
          {/* Container with fixed aspect ratio — image positioned absolutely inside */}
          <div
            ref={containerRef}
            onMouseDown={startDrag}
            onTouchStart={startDrag}
            style={{
              width: '100%',
              aspectRatio: `${BANNER_ASPECT}`,
              overflow: 'hidden',
              borderRadius: 6,
              border: '2px solid var(--primary, #1976d2)',
              cursor: 'grab',
              position: 'relative',
              background: '#000',
              userSelect: 'none',
            }}
          >
            {imgNatural && containerSize && (
              <img
                src={src}
                alt=""
                draggable={false}
                style={{
                  position: 'absolute',
                  left: offset.x,
                  top: offset.y,
                  width: imgDisplayW,
                  height: imgDisplayH,
                  maxWidth: 'none',
                  pointerEvents: 'none',
                  display: 'block',
                }}
              />
            )}
            {/* Probe image — reads natural dimensions then hides */}
            {!imgNatural && (
              <img
                src={src}
                alt=""
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                onLoad={e => {
                  const el = e.currentTarget;
                  setImgNatural({ w: el.naturalWidth, h: el.naturalHeight });
                }}
              />
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '8px 16px 16px' }}>
          <button
            onClick={onCancel}
            style={{ ...btnBase, padding: '8px 18px', fontSize: 13 }}
          >
            {t('common.cancel', 'Cancelar')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!ready}
            style={{
              padding: '8px 18px', borderRadius: 6, border: 'none',
              background: 'var(--primary, #1976d2)', color: 'var(--button-primary-foreground, #fff)',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              opacity: !ready ? 0.5 : 1,
            }}
          >
            {t('channel.crop_apply', 'Aplicar recorte')}
          </button>
        </div>
      </div>
    </div>
    </BodyPortal>
  );
};

interface ChannelSettingsPanelProps {
  onChannelReady?: (channel: Channel) => void;
  onClose?: () => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--controls-border, var(--theme-border, #d1d5db))',
  background: 'var(--controlsBg, var(--card-bg, #fff))',
  color: 'var(--controlsForeground, var(--text-primary, #111))',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary, #6b7280)',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: 20,
};

const VisibilityButton: React.FC<{
  value: ChannelVisibility;
  current: ChannelVisibility;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}> = ({ value, current, onClick, label, icon }) => (
  <button
    onClick={onClick}
    aria-pressed={current === value}
    style={{
      flex: 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      padding: '8px 6px',
      borderRadius: 6,
      border: current === value
        ? '2px solid var(--theme-primary, #1976d2)'
        : '1px solid var(--theme-border, #e5e7eb)',
      background: current === value
        ? 'color-mix(in srgb, var(--theme-primary, #1976d2) 8%, transparent)'
        : 'var(--surface-hover, #f9fafb)',
      color: current === value
        ? 'var(--theme-primary, #1976d2)'
        : 'var(--text-secondary, #6b7280)',
      fontSize: 12, fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.15s ease',
    }}
  >
    {icon}
    {label}
  </button>
);

const ChannelSettingsPanel: React.FC<ChannelSettingsPanelProps> = ({ onChannelReady, onClose }) => {
  const { t } = useTranslation();
  const { channel, loading, error, createChannel, updateChannel, setVisibility, deleteChannel } = useChannel();

  const [form, setForm] = useState({
    displayName: '',
    description: '',
    subject: '',
    language: '',
    category: '',
    level: '',
    tagsInput: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [localBannerUrl, setLocalBannerUrl] = useState<string | null>(null);
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | null>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  // Banner crop modal state
  const [cropSrc, setCropSrc] = useState<string | null>(null);  // object URL of pending banner file

  // ── Member management state ──────────────────────────────────────────────
  const [members, setMembers] = useState<{ email: string; addedAt: string }[]>([]);
  const [memberInput, setMemberInput] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);

  const { copying, regenerating, showPrivateNotice, dismissPrivateNotice, copyShareLink, regenerateShareLink } = useChannelShare((updated) => {
    onChannelReady?.(updated);
  });

  // Load members when channel is a private channel owned by the current user
  const loadMembers = useCallback(async (channelId: string) => {
    const result = await apiClient.listChannelMembers(channelId);
    if (result.data) setMembers(result.data);
  }, []);

  const handleAddMember = async () => {
    if (!channel || !memberInput.trim()) return;
    const email = memberInput.trim().toLowerCase();
    setMemberError(null);
    setAddingMember(true);
    const result = await apiClient.addChannelMember(channel.id, email);
    setAddingMember(false);
    if (result.data) { setMembers(result.data); setMemberInput(''); }
    else setMemberError(result.error?.message ?? 'Error adding member');
  };

  const handleRemoveMember = async (email: string) => {
    if (!channel) return;
    const result = await apiClient.removeChannelMember(channel.id, email);
    if (result.data) setMembers(result.data);
  };

  // Sync form when channel loads — do NOT call onChannelReady here to avoid
  // the re-fetch loop (onChannelReady → parent refresh() → loading flash → unmount → remount)
  React.useEffect(() => {
    if (channel) {
      setForm({
        displayName: channel.displayName,
        description: channel.description ?? '',
        subject: channel.subject ?? '',
        language: channel.language ?? '',
        category: channel.category ?? '',
        level: channel.level ?? '',
        tagsInput: channel.tags.join(', '),
      });
      // Load member list when channel is private and owned by this user
      if (channel.visibility === 'private') loadMembers(channel.id);
    }
  }, [channel?.id, channel?.visibility, loadMembers]);

  // Reload members whenever visibility changes to/from private
  useEffect(() => {
    if (channel?.visibility === 'private') loadMembers(channel.id);
    else setMembers([]);
  }, [channel?.visibility, channel?.id, loadMembers]);

  const parseTags = (raw: string) =>
    raw.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);

  const handleCreateOrSave = async () => {
    if (!form.displayName.trim()) {
      setLocalError(t('channel.name_required_error'));
      return;
    }
    setSaving(true);
    setLocalError(null);
    const payload = {
      displayName: form.displayName.trim(),
      description: form.description,
      subject: form.subject,
      language: form.language,
      category: form.category,
      level: form.level || undefined,
      tags: parseTags(form.tagsInput),
    };

    let result: Channel | null;
    if (!channel) {
      result = await createChannel(payload);
    } else {
      result = await updateChannel(payload);
    }

    if (result) {
      setSaveOk(true);
      onChannelReady?.(result);
      setTimeout(() => setSaveOk(false), 2000);
    } else {
      setLocalError(error ?? t('channel.save_error'));
    }
    setSaving(false);
  };

  const handleImageUpload = async (blob: Blob, type: 'avatar' | 'banner') => {
    if (!channel) return;
    const setter = type === 'banner' ? setUploadingBanner : setUploadingAvatar;
    setter(true);
    setLocalError(null);
    // Convert Blob → File so the API client receives the expected type.
    const file = blob instanceof File ? blob : new File([blob], `${type}.jpg`, { type: blob.type || 'image/jpeg' });
    const result = await apiClient.uploadChannelImage(channel.id, file, type);
    if (result.data) {
      if (type === 'banner') setLocalBannerUrl(result.data.bannerUrl ?? null);
      else setLocalAvatarUrl(result.data.avatarUrl ?? null);
      onChannelReady?.(result.data);
    } else {
      setLocalError(result.error?.message ?? t('channel.upload_error', 'Error al subir la imagen'));
    }
    setter(false);
  };

  /** Removes the avatar or banner (the channel shows its initials / default background). */
  const handleImageRemove = async (type: 'avatar' | 'banner') => {
    if (!channel) return;
    const setter = type === 'banner' ? setUploadingBanner : setUploadingAvatar;
    setter(true);
    setLocalError(null);
    const result = await apiClient.removeChannelImage(channel.id, type);
    if (result.data) {
      // '' (not null): null means "use the channel's saved image".
      if (type === 'banner') setLocalBannerUrl('');
      else setLocalAvatarUrl('');
      onChannelReady?.(result.data);
    } else {
      setLocalError(result.error?.message ?? t('channel.remove_image_error', 'Could not remove the image.'));
    }
    setter(false);
  };
  const hasBanner = !!(localBannerUrl ?? channel?.bannerUrl);
  const hasAvatar = !!(localAvatarUrl ?? channel?.avatarUrl);

  // Called when the user picks a banner file — open the crop modal instead of uploading directly.
  const handleBannerFileSelected = (file: File) => {
    const url = URL.createObjectURL(file);
    setCropSrc(url);
  };

  // Called when user confirms the crop — receives the rendered canvas blob.
  const handleCropConfirm = async (blob: Blob) => {
    if (cropSrc) { URL.revokeObjectURL(cropSrc); }
    setCropSrc(null);
    await handleImageUpload(blob, 'banner');
  };

  const handleCropCancel = () => {
    if (cropSrc) { URL.revokeObjectURL(cropSrc); }
    setCropSrc(null);
  };

  const handleVisibility = async (vis: ChannelVisibility) => {
    if (!channel || togglingVisibility) return;
    setTogglingVisibility(true);
    await setVisibility(vis);
    setTogglingVisibility(false);
  };

  const handleDelete = async () => {
    if (!channel) return;
    const ok = await deleteChannel();
    if (ok) setConfirmDelete(false);
  };

  if (loading) {
    return (
      <div style={{ padding: 24, color: 'var(--text-secondary, #6b7280)', fontSize: 13 }}>
        {t('channel.loading')}
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 20px 32px', overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h3 style={{
          margin: 0,
          fontSize: 15, fontWeight: 700,
          color: 'var(--card-title-color, var(--text-primary, #111))',
        }}>
          {channel ? t('channel.settings_title') : t('channel.settings_create_title')}
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            aria-label={t('channel.settings_close')}
            title={t('channel.settings_close')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, borderRadius: '50%',
              border: '1px solid var(--theme-border, #e5e7eb)',
              background: 'var(--surface-hover, #f3f4f6)',
              color: 'var(--text-secondary, #6b7280)',
              cursor: 'pointer', fontSize: 16, lineHeight: 1,
              flexShrink: 0,
              transition: 'background 0.15s ease, color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--theme-primary, #1976d2)';
              (e.currentTarget as HTMLButtonElement).style.color = '#fff';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--theme-primary, #1976d2)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-hover, #f3f4f6)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary, #6b7280)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--theme-border, #e5e7eb)';
            }}
          >
            ×
          </button>
        )}
      </div>

      {(localError || error) && (
        <div style={{
          marginBottom: 14, padding: '8px 12px',
          borderRadius: 6, background: '#fee2e2',
          color: '#b91c1c', fontSize: 12,
        }}>
          {localError || error}
        </div>
      )}

      {/* ── Channel images (banner + avatar) — only shown when editing existing channel ── */}
      {channel && (
        <div style={{ marginBottom: 24 }}>
          {/* Banner */}
          <label style={labelStyle}>{t('channel.banner_label', 'PORTADA')}</label>
          <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{
              height: 88,
              background: (localBannerUrl ?? channel.bannerUrl)
                ? `url(${localBannerUrl ?? channel.bannerUrl}) center/cover no-repeat`
                : 'linear-gradient(135deg, var(--theme-primary, #1976d2) 0%, #764ba2 100%)',
              display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
              padding: 8,
            }} />
            {uploadingBanner && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.45)', color: '#fff',
              }} aria-hidden>
                <Loader2 size={22} className="animate-spin" />
              </div>
            )}
            {hasBanner && (
              <button
                type="button"
                disabled={uploadingBanner}
                onClick={() => void handleImageRemove('banner')}
                aria-label={t('channel.remove_banner', 'Remove cover')}
                title={t('channel.remove_banner', 'Remove cover')}
                style={{
                  position: 'absolute', bottom: 8, left: 8,
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 10px', borderRadius: 6,
                  background: 'rgba(0,0,0,0.55)', color: '#fff',
                  border: 'none', fontSize: 11, fontWeight: 500, cursor: 'pointer',
                  backdropFilter: 'blur(4px)',
                  opacity: uploadingBanner ? 0.6 : 1,
                }}
              >
                <Trash2 size={12} aria-hidden />
                {t('channel.remove', 'Remove')}
              </button>
            )}
            <button
              type="button"
              disabled={uploadingBanner}
              onClick={() => bannerInputRef.current?.click()}
              aria-label={t('channel.change_banner_aria', 'Cambiar portada')}
              style={{
                position: 'absolute', bottom: 8, right: 8,
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 6,
                background: 'rgba(0,0,0,0.55)', color: '#fff',
                border: 'none', fontSize: 11, fontWeight: 500, cursor: 'pointer',
                backdropFilter: 'blur(4px)',
                opacity: uploadingBanner ? 0.6 : 1,
              }}
            >
              <Camera size={12} aria-hidden />
              {uploadingBanner ? t('channel.uploading', 'Subiendo…') : t('channel.change_banner', 'Cambiar portada')}
            </button>
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }}
              aria-label={t('channel.banner_input_aria', 'Seleccionar imagen de portada')}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleBannerFileSelected(file);
                e.target.value = '';
              }}
            />
          </div>

          {/* Avatar */}
          <label style={labelStyle}>{t('channel.avatar_label', 'AVATAR')}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
              overflow: 'hidden', position: 'relative',
              background: 'var(--theme-primary, #1976d2)',
              border: '2px solid var(--theme-border, #e5e7eb)',
            }}>
              {uploadingAvatar && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.45)', color: '#fff',
              }} aria-hidden>
                <Loader2 size={18} className="animate-spin" />
              </div>
            )}
              {(localAvatarUrl ?? channel.avatarUrl) ? (
                <img
                  src={localAvatarUrl ?? channel.avatarUrl ?? ''}
                  alt={channel.displayName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 18, color: '#fff',
                }}>
                  {channel.displayName.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>
            <button
              type="button"
              disabled={uploadingAvatar}
              onClick={() => avatarInputRef.current?.click()}
              aria-label={t('channel.change_avatar_aria', 'Cambiar avatar')}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', borderRadius: 6,
                border: '1px solid var(--theme-border, #e5e7eb)',
                background: 'var(--card-bg, #fff)',
                color: 'var(--text-primary, #111)',
                fontSize: 12, fontWeight: 500, cursor: 'pointer',
                opacity: uploadingAvatar ? 0.6 : 1,
              }}
            >
              <Camera size={12} aria-hidden />
              {uploadingAvatar ? t('channel.uploading', 'Subiendo…') : t('channel.change_avatar', 'Cambiar avatar')}
            </button>
            {hasAvatar && (
              <button
                type="button"
                disabled={uploadingAvatar}
                onClick={() => void handleImageRemove('avatar')}
                aria-label={t('channel.remove_avatar', 'Remove avatar')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '6px 12px', borderRadius: 6,
                  border: '1px solid var(--theme-border, #e5e7eb)',
                  background: 'var(--card-bg, #fff)',
                  color: 'var(--text-primary, #111)',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  opacity: uploadingAvatar ? 0.6 : 1,
                }}
              >
                <Trash2 size={12} aria-hidden />
                {t('channel.remove', 'Remove')}
              </button>
            )}
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }}
              aria-label={t('channel.avatar_input_aria', 'Seleccionar imagen de avatar')}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(file, 'avatar');
                e.target.value = '';
              }}
            />
          </div>
        </div>
      )}

      {/* Name */}
      <div style={sectionStyle}>
        <label style={labelStyle}>{t('channel.name_label')}</label>
        <input
          style={inputStyle}
          value={form.displayName}
          onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
          placeholder={t('channel.name_placeholder')}
          aria-label={t('channel.name_aria')}
        />
      </div>

      {/* Description */}
      <div style={sectionStyle}>
        <label style={labelStyle}>{t('channel.description_label')}</label>
        <textarea
          style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder={t('channel.description_placeholder')}
          aria-label={t('channel.description_aria')}
        />
      </div>

      {/* Subject */}
      <div style={sectionStyle}>
        <label style={labelStyle}>{t('channel.subject_label')}</label>
        <input
          style={inputStyle}
          value={form.subject}
          onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
          placeholder={t('channel.subject_settings_placeholder')}
          aria-label={t('channel.subject_settings_aria')}
        />
      </div>

      {/* Tags */}
      <div style={sectionStyle}>
        <label style={labelStyle}>{t('channel.tags_label')}</label>
        <input
          style={inputStyle}
          value={form.tagsInput}
          onChange={(e) => setForm((f) => ({ ...f, tagsInput: e.target.value }))}
          placeholder={t('channel.tags_settings_placeholder')}
          aria-label={t('channel.tags_settings_aria')}
        />
      </div>

      {/* Level */}
      <div style={sectionStyle}>
        <label style={labelStyle}>{t('channel.level_label')}</label>
        <select
          style={{ ...inputStyle, cursor: 'pointer' }}
          value={form.level}
          onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}
          aria-label={t('channel.level_aria')}
        >
          <option value="">{t('channel.level_all')}</option>
          <option value="K12">{t('channel.level_k12')}</option>
          <option value="HED">{t('channel.level_hed')}</option>
          <option value="Postgraduate">{t('channel.level_postgraduate')}</option>
          <option value="General">{t('channel.level_general')}</option>
        </select>
      </div>

      {/* Language + Category */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>{t('channel.language_label')}</label>
          <input
            style={inputStyle}
            value={form.language}
            onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
            placeholder={t('channel.language_placeholder')}
            aria-label={t('channel.language_aria')}
            maxLength={5}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>{t('channel.category_label')}</label>
          <input
            style={inputStyle}
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            placeholder={t('channel.category_placeholder')}
            aria-label={t('channel.category_aria')}
          />
        </div>
      </div>

      {/* Save button */}
      <button
        onClick={handleCreateOrSave}
        disabled={saving}
        aria-label={channel ? t('channel.save_aria_update') : t('channel.save_aria_create')}
        style={{
          width: '100%', padding: '9px 16px',
          borderRadius: 6, border: 'none',
          background: 'var(--button-primary-bg, var(--theme-primary, #1976d2))',
          color: '#fff', fontWeight: 600, fontSize: 14,
          cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? 0.7 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          transition: 'opacity 0.2s',
          marginBottom: 24,
        }}
      >
        {saveOk ? <Check size={15} aria-hidden /> : <Save size={15} aria-hidden />}
        {saving ? t('channel.saving') : saveOk ? t('channel.saved') : channel ? t('channel.save_button_update') : t('channel.save_button_create')}
      </button>

      {/* Visibility & Share — only if channel exists */}
      {channel && (
        <>
          <div style={{ height: 1, background: 'var(--theme-border, #e5e7eb)', margin: '4px 0 20px' }} />

          {/* Visibility toggle */}
          <div style={sectionStyle}>
            <label style={labelStyle}>{t('channel.visibility_label')}</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <VisibilityButton
                value="public"
                current={channel.visibility}
                onClick={() => handleVisibility('public')}
                label={t('channel.visibility_public')}
                icon={<Globe size={12} aria-hidden />}
              />
              <VisibilityButton
                value="org"
                current={channel.visibility}
                onClick={() => handleVisibility('org')}
                label={t('channel.visibility_org')}
                icon={<Building2 size={12} aria-hidden />}
              />
              <VisibilityButton
                value="network"
                current={channel.visibility}
                onClick={() => handleVisibility('network')}
                label={t('channel.visibility_network')}
                icon={<Network size={12} aria-hidden />}
              />
              <VisibilityButton
                value="private"
                current={channel.visibility}
                onClick={() => handleVisibility('private')}
                label={t('channel.visibility_private')}
                icon={<Lock size={12} aria-hidden />}
              />
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--text-secondary, #9ca3af)' }}>
              {channel.visibility === 'public'  && t('channel.visibility_public_desc')}
              {channel.visibility === 'org'     && t('channel.visibility_org_desc')}
              {channel.visibility === 'network' && t('channel.visibility_network_desc')}
              {channel.visibility === 'private' && t('channel.visibility_private_desc')}
            </p>
          </div>

          {/* Share link */}
          <div style={sectionStyle}>
            <label style={labelStyle}>{t('channel.share_link_label')}</label>
            <div style={{
              display: 'flex', gap: 6,
              padding: '7px 10px',
              borderRadius: 6,
              border: '1px solid var(--theme-border, #e5e7eb)',
              background: 'var(--surface-hover, #f9fafb)',
              fontSize: 12, color: 'var(--text-secondary, #6b7280)',
              marginBottom: 8,
              wordBreak: 'break-all',
            }}>
              <Link2 size={13} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden />
              {channel.shareUrl || `…/channel/${channel.handle}`}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => copyShareLink(channel)}
                aria-label={t('channel.copy_aria')}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  padding: '7px 12px', borderRadius: 6,
                  border: '1px solid var(--theme-border, #e5e7eb)',
                  background: copying ? 'var(--theme-primary, #1976d2)' : 'var(--card-bg, #fff)',
                  color: copying ? '#fff' : 'var(--text-primary, #111)',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {copying ? <Check size={12} aria-hidden /> : <Link2 size={12} aria-hidden />}
                {copying ? t('channel.copied') : t('channel.copy')}
              </button>
              <button
                onClick={() => regenerateShareLink(channel)}
                disabled={regenerating}
                aria-label={t('channel.regenerate_aria')}
                title={t('channel.regenerate_title')}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  padding: '7px 12px', borderRadius: 6,
                  border: '1px solid var(--theme-border, #e5e7eb)',
                  background: 'var(--card-bg, #fff)',
                  color: 'var(--text-primary, #111)',
                  fontSize: 12, fontWeight: 500,
                  cursor: regenerating ? 'not-allowed' : 'pointer',
                  opacity: regenerating ? 0.6 : 1,
                }}
              >
                <RefreshCw size={12} aria-hidden />
                {regenerating ? t('channel.regenerating') : t('channel.regenerate')}
              </button>
            </div>

            {/* Private channel notice — shown after copying */}
            {showPrivateNotice && channel.visibility === 'private' && (
              <div style={{
                marginTop: 10,
                display: 'flex', alignItems: 'flex-start', gap: 8,
                background: 'color-mix(in srgb, #f59e0b 8%, transparent)',
                border: '1px solid rgba(245,158,11,0.35)',
                borderLeft: '3px solid #f59e0b',
                borderRadius: 6,
                padding: '8px 10px',
              }}>
                <Lock size={13} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} aria-hidden />
                <p style={{ margin: 0, fontSize: 12, color: 'var(--foreground, #111)', lineHeight: 1.5, flex: 1 }}>
                  <strong>{t('channel.private_notice_title', 'Canal privado')}: </strong>
                  {t('channel.private_notice_body', 'Solo los miembros que añadas aquí podrán ver este canal y sus videos.')}
                </p>
                <button
                  onClick={dismissPrivateNotice}
                  aria-label={t('common.dismiss', 'Cerrar')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--textSecondary, #6b7280)', flexShrink: 0 }}
                >
                  <X size={12} />
                </button>
              </div>
            )}
          </div>

          {/* ── Invited members (only for private channels) ── */}
          {channel?.visibility === 'private' && (
            <div style={{ ...sectionStyle, marginTop: 4 }}>
              <div style={{ height: 1, background: 'var(--theme-border, #e5e7eb)', margin: '4px 0 20px' }} />
              <label style={labelStyle}>
                <UserPlus size={11} aria-hidden style={{ marginRight: 4, verticalAlign: 'middle' }} />
                {t('channel.members_title', 'Miembros invitados')}
              </label>

              {/* Add member input */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <input
                  type="email"
                  value={memberInput}
                  onChange={(e) => setMemberInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddMember()}
                  placeholder={t('channel.member_email_placeholder', 'correo@organización.com')}
                  aria-label={t('channel.member_email_placeholder', 'correo@organización.com')}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  onClick={handleAddMember}
                  disabled={addingMember || !memberInput.trim()}
                  aria-label={t('channel.add_member', 'Agregar miembro')}
                  style={{
                    padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                    background: 'var(--theme-primary, #1976d2)',
                    color: 'var(--button-primary-foreground, #fff)', border: 'none', cursor: addingMember ? 'not-allowed' : 'pointer',
                    opacity: addingMember || !memberInput.trim() ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {addingMember ? '…' : t('channel.add_member', 'Agregar')}
                </button>
              </div>

              {memberError && (
                <p style={{ fontSize: 12, color: 'var(--theme-danger, #ef4444)', margin: '0 0 8px' }}>
                  {memberError}
                </p>
              )}

              {/* Member list */}
              {members.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', margin: 0 }}>
                  {t('channel.no_members', 'Aún no hay miembros invitados. Solo tú puedes ver este canal.')}
                </p>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {members.map((m) => (
                    <li key={m.email} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '6px 10px', borderRadius: 6,
                      background: 'var(--surface-hover, #f3f4f6)',
                      fontSize: 13,
                    }}>
                      <span style={{ color: 'var(--text-primary, #111)', wordBreak: 'break-all' }}>{m.email}</span>
                      <button
                        onClick={() => handleRemoveMember(m.email)}
                        aria-label={`${t('channel.remove_member', 'Quitar')} ${m.email}`}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--text-secondary, #6b7280)', padding: '2px 4px',
                          flexShrink: 0, marginLeft: 8,
                          display: 'flex', alignItems: 'center',
                        }}
                      >
                        <X size={14} aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Danger zone */}
          <div style={{ height: 1, background: 'var(--theme-border, #e5e7eb)', margin: '4px 0 20px' }} />
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              aria-label={t('channel.delete_aria')}
              style={{
                width: '100%', padding: '8px 16px', borderRadius: 6,
                border: '1px solid #fca5a5',
                background: 'transparent', color: '#ef4444',
                fontSize: 12, fontWeight: 500, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              }}
            >
              <Trash2 size={13} aria-hidden />
              {t('channel.delete_button')}
            </button>
          ) : (
            <div style={{ background: '#fee2e2', padding: 12, borderRadius: 6, border: '1px solid #fca5a5' }}>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                {t('channel.delete_confirm_msg')}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleDelete}
                  aria-label={t('channel.delete_confirm_aria')}
                  style={{
                    flex: 1, padding: '7px', borderRadius: 6, border: 'none',
                    background: '#ef4444', color: '#fff',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  {t('channel.delete_yes')}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  aria-label={t('channel.delete_cancel_aria')}
                  style={{
                    flex: 1, padding: '7px', borderRadius: 6,
                    border: '1px solid var(--theme-border, #e5e7eb)',
                    background: 'var(--card-bg, #fff)',
                    color: 'var(--text-primary, #111)',
                    fontSize: 12, cursor: 'pointer',
                  }}
                >
                  {t('channel.delete_cancel')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Banner crop modal — rendered outside the scrollable panel via portal-like positioning */}
      {cropSrc && (
        <BannerCropModal
          src={cropSrc}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
};

export default ChannelSettingsPanel;
